import { motion } from "framer-motion";
import { Play } from "lucide-react";

/**
 * VideoPosterTile — a deliberately plain video preview tile.
 *
 *   • Dark glass card · soft border, no busy chrome
 *   • Subtle brand-coloured glow underneath that intensifies on hover
 *   • Centred play button with a slow pulsing ring
 *   • Click anywhere to fire `onClick` (which the parent uses to open the
 *     VideoLightbox in portrait)
 *
 * Each tile is portrait-leaning (9:16) since the source content is
 * vertical mobile-first video.
 */
export default function VideoPosterTile({
  label,
  caption,
  accentColor = "#96C11F",
  posterUrl,
  onClick,
  testId,
  delay = 0,
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 24, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1.0, delay, ease: [0.16, 1, 0.3, 1] }}
      whileHover={{ y: -6, scale: 1.015 }}
      whileTap={{ scale: 0.98 }}
      className="relative rounded-2xl overflow-hidden text-left group"
      style={{
        background: "rgba(255,255,255,0.04)",
        border: `1px solid ${accentColor}33`,
        boxShadow: `0 30px 80px -30px rgba(0,0,0,0.55), 0 0 0 0 ${accentColor}00`,
      }}
      data-testid={testId}
    >
      {/* Portrait poster area */}
      <div
        className="relative w-full bg-black overflow-hidden"
        style={{ aspectRatio: "9 / 16" }}
      >
        {/* Optional poster image — falls back to a quiet gradient */}
        {posterUrl ? (
          <img
            src={posterUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-95"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : null}
        {/* Quiet base gradient (sits under the image for graceful fallback) */}
        <div
          className="absolute inset-0"
          style={{
            background: posterUrl
              ? "linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.55) 100%)"
              : `linear-gradient(135deg, ${accentColor}1f 0%, rgba(7,18,12,0.9) 70%)`,
          }}
        />

        {/* Centred play button — pulsing ring */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <motion.span
            className="absolute rounded-full"
            style={{
              width: 96,
              height: 96,
              border: `1.5px solid ${accentColor}88`,
            }}
            animate={{ scale: [1, 1.5], opacity: [0.55, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
          />
          <div
            className="relative w-[72px] h-[72px] rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              background: "rgba(255,255,255,0.92)",
              boxShadow: `0 12px 40px rgba(0,0,0,0.45), 0 0 24px ${accentColor}66`,
            }}
          >
            <Play
              className="w-6 h-6"
              fill="#0d0d0f"
              style={{ color: "#0d0d0f", marginLeft: 3 }}
              strokeWidth={0}
            />
          </div>
        </div>
      </div>

      {/* Footer label — kept very plain */}
      <div className="px-5 py-4">
        <div
          className="text-[10px] tracking-[0.32em] uppercase whitespace-nowrap"
          style={{ color: accentColor, fontWeight: 700 }}
        >
          {label}
        </div>
        {caption && (
          <div className="text-sm font-semibold text-white/85 mt-1">
            {caption}
          </div>
        )}
      </div>
    </motion.button>
  );
}
